// @todo enable the following disabled rules see OPENTOK-31136 for more info
/* eslint-disable no-param-reassign, global-require */
import sessionObjectsDefault from './objects';
import SessionFactory from './index';

export default function initSessionFactory(deps = {}) {
  const Session = deps.Session || SessionFactory();
  const sessionObjects = deps.sessionObjects || sessionObjectsDefault;

  /**
  * The first step in using the Vonage Video API is to call the <code>OT.initSession()</code>
  * method. Other methods of the OT object check for system requirements and set up error logging.
  *
  * @class OT
  */

  /**
  * <p class="mSummary">
  * Initializes and returns the local session object for a specified session ID.
  * </p>
  * <p>
  * You connect to a Vonage Video API session using the <code>connect()</code> method
  * of the Session object returned by the <code>OT.initSession()</code> method.
  * Note that calling <code>OT.initSession()</code> does not initiate communications
  * with the cloud. It simply initializes the Session object that you can use to
  * connect (and to perform other operations once connected).
  * </p>
  *
  * <p>
  * Note that you can initialize only one Session object per session ID. Calling
  * <code>OT.initSession()</code> a second time with the same session ID returns the
  * previously created Session object. However, you can initialize multiple Session objects
  * for different sessions (with different session IDs) to connect to multiple sessions.
  * </p>
  *
  * <p>
  * When the client disconnects from the session (and the session dispatches a
  * <code>sessionDisconnected</code> event), you can reuse the Session object to
  * reconnect to the session. However, the Session object will retain any existing
  * event handlers attached to it. You can remove all event handlers by calling the
  * <code>off()</code> method of the Session object (passing in no parameters).
  * </p>
  *
  *  <p>
  *    For an example, see <a href="Session.html#connect">Session.connect()</a>.
  *  </p>
  *
  * @method OT.initSession
  * @memberof OT
  * @param {String} apiKey Your Vonage Video API key (see your <a href="https://tokbox.com/account">Vonage Video API
  * account page</a>).
  * @param {String} sessionId The session ID identifying the Vonage Video API session. For more
  * information, see <a href="https://tokbox.com/developer/guides/create-session/">Session
  * creation</a>.
  * @param {Object} options An object that includes optional options for initializing the session.
  * This object includes the following properties:
  * <ul>
  *   <li><code>connectionEventsSuppressed</code> &mdash; (Boolean)
  *     Set this property to <code>true</code> to support large interactive video sessions.
  *     This prevents the Session object from dispatching <code>connectionCreated</code> and
  *     <code>connectionDestroyed</code> events when other clients connect to or disconnect from
  *     the session. (Also, the Vonage Video API server does not send these events to the web client.)
  *     For more information, see
  *     <a href="https://tokbox.com/developer/guides/broadcast/live-interactive-video/#suppressing-connection-events">
  *     Suppressing connection events</a> in the Vonage Video API developer guides.
  *   </li>
  *   <li><code>ipWhitelist</code> &mdash; (Boolean) This is available as an
  *     <a href="https://www.vonage.com/communications-apis/video/pricing/" target="_blank">add-on feature</a>.
  *     Set this to <code>true</code> if IP white listing is enabled for your project.
  *     The default value is <code>false</code>.
  *   </li>
  *   <li><code>iceConfig</code> &mdash; (Object) This object includes the following properties:
  *    <p>
  *    <ul>
  *     <li>
  *        <code>includeServers</code> (String) &mdash; Set this to <code>'custom'</code> and client will use only the custom TURN servers you provide in the <code>customServers</code> array. Set this to <code>'all'</code> (the default) and the client will use both the custom TURN servers you provide along with OpenTok TURN servers.
  *     </li>
  *     <li>
  *        <code>transportPolicy</code> (String) &mdash; Set this to <code>'all'</code> (the default) and the client will use all ICE transport types (such as host, srflx, and TURN) to establish media connectivity. Set this to <code>'relay'</code> to force connectivity through TURN always and ignore all other ICE candidates.
  *     </li>
  *     <li>
  *        <code>customServers</code> (Array) &mdash; Set this to an array of objects defining your custom TURN servers. Each object corresponds to one custom TURN server, and it includes the following properties: <code>urls</code> &mdash; a string or an array of strings, where each string is a URL supported by the TURN server (and this may be only one URL); <code>username</code> &mdash; the username (string) for the TURN server defined in this object; <code>credential</code> &mdash; the credential string for the TURN server defined in this object.
  *     </li>
  *    </ul>
  *  </p>
  *  <p>
  * This feature is part of the configurable TURN
  *     <a href="https://www.vonage.com/communications-apis/video/pricing/" target="_blank">add-on feature</a>. For more information
  * and a code sample, see the
  * <a href="https://tokbox.com/developer/guides/configurable-turn-servers/">Configurable TURN servers</a>
  * developer guide.
  * </p>
  *   </li>
  *   <li><code>encryptionSecret</code> &mdash; (String) This is the initial encryption secret used by the
  *   end-to-end encryption feature. See <a href="https://tokbox.com/developer/guides/end-to-end-encryption/">
  *   the developer guide</a>.
  *   </li>
  *   <li><code>singlePeerConnection</code> &mdash; (Boolean) Enables the
  *   <a href="https://tokbox.com/developer/guides/create-session/#single-peer-connection">Single Peer Connection</a>
  *   (SPC) feature. The default is <code>false</code> (SPC is disabled).
  *   </li>
  *   <li><code>sessionMigration</code> &mdash; (Boolean) Enables the session migration feature,
  *   allowing the client to to remain connected during server rotation. The default value is <code>false</code>
  *   (session migration is not enabled). For more information, see
  *   <a href="https://tokbox.com/developer/guides/server-rotation/">Server Rotation and Session Migration</a>.
  *   <p>
  *   <i>This is a beta feature.</i>
  *   </p>
  *   </li>
  * </ul>
  *
  * @returns {Session} The session object through which all further interactions with
  * the session will occur.
  */
  return function initSession(apiKey, sessionId, opt) {
    if (sessionId == null) {
      sessionId = apiKey;
      apiKey = null;
    }

    // Allow buggy legacy behavior to succeed, where the client can connect if sessionId
    // is an array containing one element (the session ID), but fix it so that sessionId
    // is stored as a string (not an array):
    if (Array.isArray(sessionId) && sessionId.length === 1) {
      sessionId = sessionId[0];
    }

    let session = sessionObjects.sessions.get(sessionId);

    if (!session) {
      session = new Session(apiKey, sessionId, opt);
      sessionObjects.sessions.add(session);
    }

    return session;
  };
}
